import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RolInvitableFormulario } from '../../../../../core/models/invitaciones.models';
import { RolPlataforma } from '../../../../../core/models/novedades.models';
import { DatosPersonales } from '../../../../../core/models/perfil.models';
import { Postulacion, RegistroParticipanteFormulario } from '../../../../../core/models/postulaciones.models';
import { InvitacionesService } from '../../../../../core/services/invitaciones.service';
import { PostulacionesLogService } from '../../../../../core/services/postulaciones-log.service';
import { FormularioEditable } from '../postulacion-formulario.models';

interface InvitadoFormulario {
  nombres: string;
  apellidos: string;
  rut: string;
  email: string;
  institucionAsociada: string;
}

type RolEquipoVista = RegistroParticipanteFormulario['rol'] | 'postulante';

interface RegistroParticipanteVista extends Omit<RegistroParticipanteFormulario, 'rol'> {
  rol: RolEquipoVista;
}

@Component({
  selector: 'app-postulacion-paso-equipo-trabajo',
  imports: [CommonModule, FormsModule],
  templateUrl: './postulacion-paso-equipo-trabajo.html',
  styleUrl: './postulacion-formulario-paso.shared.css',
})
export class PostulacionPasoEquipoTrabajo {
  @Input({ required: true }) formulario!: FormularioEditable;
  @Input() postulacion: Postulacion | null = null;
  @Input() perfil: DatosPersonales | null = null;
  @Input() rol: RolPlataforma = 'postulante';
  @Input() soloLectura = false;
  @Output() changed = new EventEmitter<void>();

  formularioInvitacionVisible = false;
  enviando = false;
  rolSeleccionado: RolInvitableFormulario | '' = '';
  rolInvitacionActiva: RolInvitableFormulario = 'recomendador';
  mensajeInvitacion = '';
  invitado: InvitadoFormulario = this.crearInvitadoVacio();

  constructor(
    private readonly invitacionesService: InvitacionesService,
    private readonly postulacionesLogService: PostulacionesLogService
  ) {}

  registrarCambio(): void {
    this.changed.emit();
  }

  get puedeInvitar(): boolean {
    return !this.soloLectura && this.rol === 'postulante';
  }

  get puedeAbrirFormularioInvitacion(): boolean {
    return this.puedeInvitar && this.rolSeleccionado.length > 0 && this.institucionesDisponibles.length > 0;
  }

  get institucionesDisponibles(): string[] {
    if (!this.formulario) return [];

    const institucionesRegistradas = this.formulario.instituciones
      .map((institucion) => institucion.nombreInstitucion.trim())
      .filter(Boolean);
    const institucionPrincipal = this.formulario.institucionPatrocinante.trim();

    return [...new Set([...institucionesRegistradas, institucionPrincipal].filter(Boolean))];
  }

  get registrosParticipacion(): RegistroParticipanteVista[] {
    if (!this.formulario) return [];

    const registros =
      this.formulario.registroParticipantes.length > 0
        ? this.formulario.registroParticipantes.map((registro) => this.normalizarRegistroParticipante(registro))
        : this.crearRegistrosInferidos();

    return [this.crearRegistroPostulante(), ...registros];
  }

  private crearRegistrosInferidos(): RegistroParticipanteVista[] {
    const registros: RegistroParticipanteFormulario[] = [];

    if (this.formulario.recomendadorAsignado.trim()) {
      registros.push(this.crearRegistroInferido('recomendador', this.formulario.recomendadorAsignado, 'pendiente'));
    }

    if (this.formulario.colaboradorAsignado.trim()) {
      registros.push(this.crearRegistroInferido('colaborador', this.formulario.colaboradorAsignado, 'pendiente'));
    }

    if (this.formulario.patrocinadorAsignado.trim()) {
      registros.push(
        this.crearRegistroInferido(
          'patrocinador',
          this.formulario.patrocinadorAsignado,
          this.formulario.patrocinioSolicitado === 'confirmado' ? 'confirmado' : 'solicitado'
        )
      );
    }

    return registros.map((registro) => this.normalizarRegistroParticipante(registro));
  }

  abrirFormularioInvitacion(): void {
    if (!this.rolSeleccionado) return;

    if (this.institucionesDisponibles.length === 0) {
      this.mensajeInvitacion = 'Primero registra al menos una institucion para asociar el integrante.';
      return;
    }

    this.abrirPanelInvitacion(this.rolSeleccionado);
  }

  abrirPanelInvitacion(rolInvitable: RolInvitableFormulario): void {
    if (!this.puedeInvitar) return;

    this.rolInvitacionActiva = rolInvitable;
    this.invitado = this.crearInvitadoVacio();
    this.formularioInvitacionVisible = true;
    this.mensajeInvitacion = '';
  }

  cerrarPanelInvitacion(): void {
    this.formularioInvitacionVisible = false;
    this.enviando = false;
  }

  enviarInvitacion(): void {
    if (!this.postulacion || this.enviando) return;

    const institucionAsociada = this.invitado.institucionAsociada.trim();
    if (!institucionAsociada) {
      this.mensajeInvitacion = 'Selecciona la institucion asociada antes de enviar la invitacion.';
      return;
    }

    this.enviando = true;
    const postulacionActual = this.postulacion;
    const nombreCompleto = `${this.invitado.nombres.trim()} ${this.invitado.apellidos.trim()}`.trim();

    if (this.rolInvitacionActiva === 'patrocinador') {
      this.formulario.patrocinadorAsignado = nombreCompleto || this.invitado.email.trim();
      this.formulario.patrocinioSolicitado = 'solicitado';
      this.actualizarRegistroParticipante({
        rol: 'patrocinador',
        nombres: this.invitado.nombres.trim(),
        apellidos: this.invitado.apellidos.trim(),
        rut: this.invitado.rut.trim(),
        email: this.invitado.email.trim(),
        institucionAsociada,
        estado: 'solicitado',
        fechaSolicitud: new Date().toISOString(),
      });
      this.registrarCambio();
      this.postulacionesLogService.registrar({
        timestamp: new Date().toISOString(),
        rol: 'postulante',
        idPostulacion: postulacionActual.idPostulacion,
        folio: postulacionActual.folio,
        accion: 'solicitar_patrocinio',
        detalle: `Solicitud de patrocinio enviada a ${nombreCompleto || this.invitado.email.trim()}.`,
      });
      this.enviando = false;
      this.formularioInvitacionVisible = false;
      this.rolSeleccionado = '';
      this.mensajeInvitacion = 'Solicitud de patrocinio enviada y registrada en el log.';
      return;
    }

    this.invitacionesService
      .crearInvitacion({
        idPostulacion: postulacionActual.idPostulacion,
        concurso: postulacionActual.concurso,
        proyecto: postulacionActual.nombreProyecto,
        subdireccion: postulacionActual.subdireccion,
        folio: postulacionActual.folio,
        rolDestino: this.rolInvitacionActiva,
        nombresDestino: this.invitado.nombres.trim(),
        apellidosDestino: this.invitado.apellidos.trim(),
        rutDestino: this.invitado.rut.trim(),
        emailDestino: this.invitado.email.trim(),
        institucionAsociada,
      })
      .subscribe((invitacion) => {
        this.enviando = false;

        if (!invitacion) {
          this.mensajeInvitacion = 'No fue posible registrar la invitacion en esta etapa.';
          return;
        }

        if (this.rolInvitacionActiva === 'recomendador') {
          this.formulario.recomendadorAsignado = nombreCompleto || this.invitado.email.trim();
        } else {
          this.formulario.colaboradorAsignado = nombreCompleto || this.invitado.email.trim();
        }

        this.actualizarRegistroParticipante({
          idInvitacion: invitacion.id,
          rol: this.rolInvitacionActiva,
          nombres: this.invitado.nombres.trim(),
          apellidos: this.invitado.apellidos.trim(),
          rut: this.invitado.rut.trim(),
          email: this.invitado.email.trim(),
          institucionAsociada,
          estado: 'pendiente',
          fechaSolicitud: new Date().toISOString(),
        });

        this.registrarCambio();
        this.postulacionesLogService.registrar({
          timestamp: new Date().toISOString(),
          rol: 'postulante',
          idPostulacion: postulacionActual.idPostulacion,
          folio: postulacionActual.folio,
          accion: this.rolInvitacionActiva === 'recomendador' ? 'invitar_recomendador' : 'invitar_colaborador',
          detalle: `Invitacion enviada a ${nombreCompleto || this.invitado.email.trim()} para rol ${this.etiquetaRol(this.rolInvitacionActiva)}.`,
        });
        this.formularioInvitacionVisible = false;
        this.rolSeleccionado = '';
        this.mensajeInvitacion = `Invitacion enviada a ${nombreCompleto || this.invitado.email.trim()} y notificacion registrada.`;
      });
  }

  tituloInvitacion(): string {
    if (this.rolInvitacionActiva === 'recomendador') return 'Invitar recomendador';
    if (this.rolInvitacionActiva === 'colaborador') return 'Invitar integrante del equipo';
    return 'Solicitar patrocinio';
  }

  etiquetaRol(rolInvitable: RolEquipoVista): string {
    if (rolInvitable === 'postulante') return 'Postulante';
    if (rolInvitable === 'recomendador') return 'Recomendador';
    if (rolInvitable === 'colaborador') return 'Integrante del equipo';
    return 'Patrocinador';
  }

  etiquetaEstado(estado: RegistroParticipanteFormulario['estado']): string {
    if (estado === 'solicitado') return 'Solicitado';
    if (estado === 'aceptado') return 'Aceptado';
    if (estado === 'rechazado') return 'Rechazado';
    if (estado === 'confirmado') return 'Confirmado';
    return 'Pendiente';
  }

  private crearInvitadoVacio(): InvitadoFormulario {
    return {
      nombres: '',
      apellidos: '',
      rut: '',
      email: '',
      institucionAsociada: this.institucionesDisponibles[0] ?? '',
    };
  }

  private crearRegistroPostulante(): RegistroParticipanteVista {
    return {
      rol: 'postulante',
      nombres: [this.perfil?.primerNombre, this.perfil?.segundoNombre].filter(Boolean).join(' ').trim() || 'Postulante',
      apellidos: [this.perfil?.primerApellido, this.perfil?.segundoApellido].filter(Boolean).join(' ').trim(),
      rut: this.perfil?.runPasaporte ?? '',
      email: this.perfil?.correoPrincipal ?? '',
      institucionAsociada: this.institucionPrincipalEquipo(),
      estado: 'confirmado',
      fechaSolicitud: '',
    };
  }

  private normalizarRegistroParticipante(registro: RegistroParticipanteFormulario): RegistroParticipanteVista {
    return {
      ...registro,
      institucionAsociada: registro.institucionAsociada?.trim() || this.institucionPrincipalEquipo(),
    };
  }

  private institucionPrincipalEquipo(): string {
    const principal = this.formulario?.instituciones.find((institucion) => institucion.rol === 'principal');
    return principal?.nombreInstitucion.trim() || this.formulario?.institucionPatrocinante.trim() || this.institucionesDisponibles[0] || '';
  }

  private actualizarRegistroParticipante(registro: RegistroParticipanteFormulario): void {
    const emailRegistro = registro.email.trim().toLowerCase();
    const restantes = this.formulario.registroParticipantes.filter((item) => {
      const mismoRol = item.rol === registro.rol;
      const mismoEmail = item.email.trim().toLowerCase() === emailRegistro;
      return !(mismoRol && mismoEmail);
    });
    this.formulario.registroParticipantes = [registro, ...restantes];
  }

  private crearRegistroInferido(
    rol: RegistroParticipanteFormulario['rol'],
    valor: string,
    estado: RegistroParticipanteFormulario['estado']
  ): RegistroParticipanteFormulario {
    return {
      rol,
      nombres: valor,
      apellidos: '',
      rut: '',
      email: valor.includes('@') ? valor : '',
      institucionAsociada: this.institucionesDisponibles[0] ?? '',
      estado,
      fechaSolicitud: '',
    };
  }
}
